import { inject, Injectable } from "@angular/core";
import { Router } from "@angular/router";


@Injectable()
export class WidgetHtmlAnchorProcessorService {

    private router: Router = inject(Router);

    public processAnchorReferences(node: HTMLElement): void {
        if (!node) return;

        const children: ChildNode[] = Array.from(node.childNodes);

        // Recursively resolve anchor references in children nodes.
        children.flatMap(child => this.processAnchorReferences(child as HTMLElement));

        // If this node is an anchor, process its href attribute.
        if (node instanceof HTMLAnchorElement) this.processAnchorReference(node);
    }

    public processAnchorReference(anchor: HTMLAnchorElement): void {
        if (!anchor) return;

        const url: string = anchor.getAttribute("href");

        if (!url?.length || !this.isInternalUrl(url)) return;

        const target: string = anchor.getAttribute("target");

        // If target is "_blank", preserve the href for browser navigation in a new tab.
        if (target === "_blank") return;

        const relativeUrl: string = this.getRelativeUrl(url);

        if (!relativeUrl?.length) return;

        // If the url is internal, modify the anchor to make the routing client-side.
        // Preserve the original onClick event if it exists, wrapping it with the navigation.
        const onClick: (event: MouseEvent) => any = anchor.onclick;

        anchor.onclick = (event: MouseEvent): void => {
            // Execute the original onClick handler if it exists.
            if (!!onClick) onClick.call(anchor, event);

            // Navigate to the relative URL.
            this.router.navigateByUrl(relativeUrl).then();
        };

        // Remove href to avoid server side routing.
        anchor.removeAttribute("href");
    }

    public isInternalUrl(url: string): boolean {
        try {
            const targetUrl = new URL(url);

            // Verify if the URL is inside the application or not.
            return location.origin === targetUrl.origin;

        } catch {
            // Invalid URLs are treated as internal relative paths.
            return true;
        }
    }

    public getRelativeUrl(url: string): string {
        try {
            const targetUrl = new URL(url);

            // Get the relative path of the url.
            return targetUrl.pathname + targetUrl.search + targetUrl.hash;

        } catch {
            // Invalid URLs are treated as internal relative paths.
            return url;
        }
    }
}